import React, { useEffect, useState } from 'react';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { Button, Switch, Tooltip } from 'antd';
// Api
import { EDIT_CAMPAIGN_SETTINGS } from 'api/campaignV2/mutations';
// Types
import {
  GetCampaignsV2,
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2Variables,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  EditCampaignSettings,
  EditCampaignSettingsVariables,
} from 'api/campaignV2/types/EditCampaignSettings';
import { CampaignStatus } from 'api/graphql-global-types';
// Ui
import InfoTip from 'ui/InfoTip/InfoTip';
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './ChangeRequiresAgreement.module.scss';

type ChangeRequiresAgreementProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  refetch: (
    variables?: Partial<GetCampaignsV2Variables> | undefined
  ) => Promise<ApolloQueryResult<GetCampaignsV2>>;
};

/** Same as the backend: fixed from approval on, since talents sign once the campaign is live */
const EDITABLE_STATUSES = [
  CampaignStatus.DETAIL_FILLED,
  CampaignStatus.ON_REVIEW,
];

const REQUIRES_AGREEMENT_TIP =
  'When on, talents must sign the digital agreement before they can apply, and the campaign needs a brand signatory before it can be approved.';

const LOCKED_TIP =
  'Can only be changed before the campaign is approved. Talents may already have signed.';

const ChangeRequiresAgreement = ({
  record,
  refetch,
}: ChangeRequiresAgreementProps): JSX.Element => {
  const [requiresAgreement, setRequiresAgreement] = useState(
    record.requiresAgreement
  );

  useEffect(() => {
    setRequiresAgreement(record.requiresAgreement);
  }, [record.requiresAgreement]);

  const [editCampaignSettings, { loading }] = useMutation<
    EditCampaignSettings,
    EditCampaignSettingsVariables
  >(EDIT_CAMPAIGN_SETTINGS);

  const isEditable = EDITABLE_STATUSES.includes(record.status);
  const hasChanged = requiresAgreement !== record.requiresAgreement;

  const handleUpdate = async () => {
    try {
      await editCampaignSettings({
        variables: { input: { campaignId: record.id, requiresAgreement } },
      });
      successNotification(
        `Digital agreement ${
          requiresAgreement ? 'required' : 'no longer required'
        }`
      );
      refetch();
    } catch (error: any) {
      console.error('Error updating the digital agreement requirement', error);
      errorNotification(
        error?.message || 'Error updating the digital agreement requirement'
      );
    }
  };

  return (
    <div className={styles.root}>
      <p className={styles.label}>
        Requires digital agreement
        <InfoTip title={REQUIRES_AGREEMENT_TIP} />
      </p>

      <div className={styles.inputContainer}>
        <Tooltip title={isEditable ? undefined : LOCKED_TIP}>
          <Switch
            checked={requiresAgreement}
            disabled={!isEditable || loading}
            onChange={setRequiresAgreement}
          />
        </Tooltip>

        {isEditable && (
          <Button
            type="primary"
            onClick={handleUpdate}
            loading={loading}
            disabled={!hasChanged}
          >
            Update
          </Button>
        )}
      </div>

      <p className={styles.explanation}>
        {isEditable
          ? 'Can only be changed before the campaign is approved.'
          : LOCKED_TIP}
      </p>
    </div>
  );
};

export default ChangeRequiresAgreement;
